import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { sendRecommendationEmail } from "../../api/services/mailer";
import { RootState } from "..";
import { Message } from "../../types/objectTypes";

const introduction: Record<string, string> = {
  doability: `Hi there! I'm here to help you discover if homeschooling is doable \
    for your family. Let's take this first step together to see if homeschooling could \
    be a practical option for you! Shall we move forward together?`,
  style: `Hi there! Let's discover your unique homeschooling style! I'll help you \
    identify which educational approach best matches your teaching preferences and your \
    children's learning needs. Shall we move forward together?`,
  program: `Hi there! Now that we've identified your homeschooling style, let's find the perfect \
    Homeschool Global program and curriculum type for your family! I'll recommend the best match based \
    on your teaching approach, your children's learning needs, and your practical requirements. \
    Shall we move forward together?`,
};

interface DoabilityBot {
  isActive: boolean;
  key: string;
  type: "doability";
}

interface StyleBot {
  isActive: boolean;
  key: string;
  type: "style";
}

interface ChatState {
  conversationId: string;
  doability: Message[];
  style: Message[];
  program: Message[];
  doabilityBot: DoabilityBot;
  styleBot: StyleBot;
  status: "start" | "ongoing" | "reset" | "completed";
}

const initialState: ChatState = {
  conversationId: "",
  doability: [{ role: "assistant", content: introduction.doability }],
  style: [],
  program: [{ role: "assistant", content: introduction.program }],
  doabilityBot: {
    isActive: true,
    key: "",
    type: "doability",
  },
  styleBot: {
    isActive: true,
    key: "",
    type: "style",
  },
  status: "start",
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setConversationId(state, action: PayloadAction<string>) {
      state.conversationId = action.payload;
      state.status = "start";
    },
    updateDoabilityBot(state, action: PayloadAction<DoabilityBot>) {
      state.doabilityBot = action.payload;
    },
    updateStyleBot(state, action: PayloadAction<StyleBot>) {
      state.styleBot = action.payload;
    },
    updateHistory(
      state: ChatState,
      action: PayloadAction<{
        type: string;
        messages: Message[];
      }>
    ) {
      state.status = "ongoing";
      const { type, messages } = action.payload;

      if (type === "doability" || type === "style" || type === "program") {
        state[type] = messages;
      }
    },
    resetHistory(state) {
      state.doability = initialState.doability;
      state.style = initialState.style;
      state.program = initialState.program;
      state.status = "reset";
    },
    completeConversation(state) {
      state.status = "completed";
    },
  },
});

export const sendRecommendation = createAsyncThunk(
  "chat/sendRecommendation",
  async (email: string, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { doability, style, program } = state.chat;

    const getLastMessage = (messages: typeof doability): string => {
      const last = messages.at(-1);
      return last?.content ?? "";
    };

    const content = `
        Doability: ${getLastMessage(doability)}
        Style: ${getLastMessage(style)}
        Program: ${getLastMessage(program)}
      `.trim();

    try {
      return await sendRecommendationEmail({ email, content });
    } catch (error: any) {
      return rejectWithValue(error?.response || error.message);
    }
  }
);

export const {
  setConversationId,
  updateHistory,
  resetHistory,
  completeConversation,
  updateDoabilityBot,
  updateStyleBot,
} = chatSlice.actions;
export default chatSlice.reducer;
